import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaArrowLeft, FaSave, FaTimes, FaMapMarkerAlt, FaUsers, FaEdit, FaTrash } from 'react-icons/fa';

const RewardSettingsPage = () => {
  const navigate = useNavigate();
  const { baseUrl, getHeaders, user } = useContext(UserContext);
  const [conversionRate, setConversionRate] = useState(1);
  const [currency, setCurrency] = useState('AED');
  const [maxRedemptionLimit, setMaxRedemptionLimit] = useState("");
  const [pointsPerCurrency, setPointsPerCurrency] = useState("");
  const [spendUnit, setSpendUnit] = useState(1);
  const [loading, setLoading] = useState(false);
  const [settingsList, setSettingsList] = useState([]);

  // New state for Company and Branch selection
  const [companies, setCompanies] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // New state for Assignment Modal
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);

  useEffect(() => {
    // Determine permissions
    const roleNorm = (user?.role || '').toLowerCase().replace(/\s+/g, '');
    const isSA = roleNorm === 'groupadmin' || roleNorm === 'superadmin';
    const isCA = roleNorm === 'companyadmin' || roleNorm === 'admin' || roleNorm === 'manager';
    setIsSuperAdmin(isSA);
    setIsAdmin(isCA || isSA);

    // Initial setup
    const active_company = localStorage.getItem('active_company') || user?.company_name || "";
    const active_branch = localStorage.getItem('active_branch') || user?.branch_name || "";
    setSelectedCompany(active_company);
    setSelectedBranch(active_branch);

    fetchCompanies();
    fetchAllSettings();
  }, [user]);

  useEffect(() => {
    if (selectedCompanies.length > 0) {
      const allBranches = [];
      selectedCompanies.forEach(comp => {
        const compBranches = companyBranchesMap[comp];
        if (compBranches) {
          allBranches.push(...compBranches);
        }
      });
      setAvailableBranches([...new Set(allBranches)]);
    } else {
      setAvailableBranches([]);
    }
  }, [selectedCompanies, companyBranchesMap]);

  useEffect(() => {
    // Only fetch settings if we have the context we need, or wait for them
    fetchSettings();
  }, [selectedCompany, selectedBranch]);

  const fetchCompanies = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      
      let comps = [];
      if (userObj.companies && userObj.companies.length > 0) {
        comps = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (userObj.company_name || userObj.company) {
        comps = [userObj.company_name || userObj.company];
      } else {
        const res = await axios.get(`${baseUrl}/api/company-details`, { headers: getHeaders() });
        const details = res.data.companyDetails || [];
        comps = details.map(d => d.restaurantName).filter(n => n);
      }
      const finalComps = comps.length > 0 ? [...new Set(comps)] : ['POS 8'];
      setCompanies(finalComps);

      // Auto select companies
      const activeContext = localStorage.getItem('active_company');
      if (selectedCompanies.length === 0 && finalComps.length > 0) {
        if (activeContext && activeContext !== 'All' && finalComps.includes(activeContext)) {
          setSelectedCompanies([activeContext]);
        } else {
          setSelectedCompanies(finalComps);
        }
      }

      // Fetch branches aggressively
      const newMap = {};
      const allBranches = [];
      await Promise.all(finalComps.map(async (comp) => {
        try {
          const bRes = await axios.get(`${baseUrl}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: getHeaders(null, comp) });
          const branchesData = bRes.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)).filter(Boolean);
          if (!branchesData.includes('All Branches')) branchesData.unshift('All Branches');
          newMap[comp] = branchesData;
          allBranches.push(...branchesData);
        } catch (e) { console.error(`Error fetching branches for ${comp}`, e); }
      }));
      setCompanyBranchesMap(newMap);
      setAvailableBranches([...new Set(allBranches)]);
    } catch (e) { console.error(e); }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const active_company = selectedCompany || localStorage.getItem('active_company') || user?.company_name;
      const active_branch = selectedBranch || localStorage.getItem('active_branch') || user?.branch_name;

      const response = await axios.get(`${baseUrl}/api/reward-settings`, {
        headers: getHeaders(active_branch, active_company)
      });

      if (response.data && response.data.success && response.data.data) {
        setConversionRate(response.data.data.point_conversion_rate || response.data.data.conversion_rate || 1);
        setCurrency(response.data.data.currency || 'AED');
        setMaxRedemptionLimit(response.data.data.max_redemption_limit || "");
        setPointsPerCurrency(response.data.data.points_per_currency || "");
        setSpendUnit(response.data.data.spend_unit || 1);
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        setConversionRate(response.data[0].conversion_rate);
        setCurrency(response.data[0].currency || 'AED');
        setMaxRedemptionLimit(response.data[0].max_redemption_limit || "");
        setPointsPerCurrency(response.data[0].points_per_currency || "");
        setSpendUnit(response.data[0].spend_unit || 1);
      }
    } catch (error) {
      console.error("Error fetching reward settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSettings = async () => {
    try {
      const active_company = localStorage.getItem('active_company') || user?.company_name || "";
      const headers = getHeaders(null, active_company);
      const response = await axios.get(`${baseUrl}/api/reward-settings/list`, { headers });
      if (response.data && response.data.success) {
        setSettingsList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching all settings:", error);
    }
  };

  const handleEdit = (setting) => {
    setConversionRate(setting.conversion_rate || setting.point_conversion_rate || 1);
    setCurrency(setting.currency || 'AED');
    setMaxRedemptionLimit(setting.max_redemption_limit || "");
    setPointsPerCurrency(setting.points_per_currency || "");
    setSpendUnit(setting.spend_unit || 1);
    
    // Auto-select in the assignment modal
    if (setting.company_name && setting.company_name !== 'All') {
      setSelectedCompanies([setting.company_name]);
    }
    if (setting.branch_name && setting.branch_name !== 'All') {
      setSelectedBranches([setting.branch_name]);
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info("Setting loaded! Modify and click Save to apply changes.");
  };

  const handleDelete = async (settingId) => {
    if (!window.confirm("Are you sure you want to delete this reward setting?")) return;
    
    try {
      await axios.delete(`${baseUrl}/api/reward-settings/${settingId}`, { headers: getHeaders() });
      toast.success("Reward Setting deleted!");
      fetchAllSettings(); // Refresh list
    } catch (error) {
      console.error("Error deleting setting:", error);
      toast.error("Failed to delete reward setting.");
    }
  };

  const handleSave = async () => {
    if (selectedBranches.length === 0) {
      toast.error("Please select at least one branch.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        conversion_rate: parseFloat(conversionRate),
        currency: currency,
        max_redemption_limit: parseFloat(maxRedemptionLimit) || 0,
        points_per_currency: parseFloat(pointsPerCurrency) || 0,
        spend_unit: parseFloat(spendUnit) || 1
      };

      for (const branch of selectedBranches) {
        const comp = selectedCompanies.length > 0 ? selectedCompanies[0] : (localStorage.getItem('active_company') || user?.company_name);
        await axios.post(`${baseUrl}/api/reward-settings`, payload, {
          headers: getHeaders(branch, comp)
        });
      }

      toast.success("Reward Settings saved successfully!");
      setShowAssignmentModal(false);
      
      // Clear fields after save
      setConversionRate(1);
      setCurrency('AED');
      setMaxRedemptionLimit("");
      setSelectedCompanies([]);
      setSelectedBranches([]);
      
      fetchAllSettings(); // Refresh list
    } catch (error) {
      console.error("Error saving reward settings:", error);
      toast.error("Failed to save reward settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-customer-container main-customer-page">
      <ToastContainer />
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate("/admin")} className="header-back-btn">
            <FaArrowLeft /> Back to Admin
          </button>
        </div>

        <div className="header-center">
          <h1>Reward Settings</h1>
        </div>

        <div className="header-right">
          <div className="header-actions" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <button className="save-btn" onClick={() => setShowAssignmentModal(true)} disabled={loading}>
              {loading ? "Saving..." : <><FaSave /> Save</>}
            </button>
          </div>
        </div>
      </div>

      <div className="customer-content-section" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="form-sections">
          <div className="form-section-block">
            <div className="section-title">
              <h3>Point Conversion</h3>
            </div>
            <div className="form-group-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Point Conversion Rate ({currency})</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: '#F8F9FD', padding: '16px 20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                  <span style={{ fontWeight: '700', color: '#1B1B29', fontSize: '15px' }}>1 Point = </span>
                  <input
                    type="number"
                    value={conversionRate}
                    onChange={(e) => setConversionRate(e.target.value)}
                    step="0.01"
                    min="0"
                    placeholder="Enter rate"
                    style={{ flex: 1, padding: '12px 16px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '15px', background: '#ffffff' }}
                  />
                  <span style={{ fontWeight: '700', color: '#1B1B29', fontSize: '15px' }}>{currency}</span>
                </div>
                <p style={{ fontSize: '13px', color: '#64748B', marginTop: '8px', fontWeight: '500' }}>
                  This defines how much 1 reward point is worth when redeeming.
                </p>
              </div>

              <div className="form-group">
                <label>Currency</label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  placeholder="e.g. AED, INR"
                  style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '15px', width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group">
                <label>Maximum Redemption Limit (Points)</label>
                <input
                  type="number"
                  value={maxRedemptionLimit}
                  onChange={(e) => setMaxRedemptionLimit(e.target.value)}
                  min="0"
                  placeholder="Leave blank or 0 for unlimited"
                  style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '15px', width: '100%', boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: '13px', color: '#64748B', marginTop: '8px', fontWeight: '500' }}>
                  The maximum number of points a customer can redeem per order. Leave blank or enter 0 for unlimited.
                </p>
              </div>

              <div className="form-group" style={{ background: '#F0FDF4', padding: '18px 20px', borderRadius: '12px', border: '1.5px solid #86EFAC' }}>
                <label style={{ color: '#166534', fontWeight: '700', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎁 Default Reward Points for All Items (General Cart Ratio)
                </label>
                <p style={{ fontSize: '13px', color: '#15803D', marginTop: '4px', marginBottom: '14px' }}>
                  Reward points automatically earned by customers across ALL menu items if no specific rule overrides it.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '600', color: '#1E293B', fontSize: '14px' }}>Spend</span>
                  <input
                    type="number"
                    value={spendUnit}
                    onChange={(e) => setSpendUnit(e.target.value)}
                    min="1"
                    placeholder="100"
                    style={{ width: '100px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '15px', background: '#ffffff', textAlign: 'center' }}
                  />
                  <span style={{ fontWeight: '700', color: '#1E293B', fontSize: '14px' }}>{currency} = Earn</span>
                  <input
                    type="number"
                    value={pointsPerCurrency}
                    onChange={(e) => setPointsPerCurrency(e.target.value)}
                    min="0"
                    step="0.1"
                    placeholder="e.g. 10"
                    style={{ width: '110px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '15px', background: '#ffffff', textAlign: 'center' }}
                  />
                  <span style={{ fontWeight: '700', color: '#166534', fontSize: '14px' }}>Reward Points</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Added Settings List Table (Full Width) --- */}
      <div style={{ padding: '0 40px', margin: '40px 0', width: '100%', boxSizing: 'border-box' }}>
        <div className="section-title" style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1B1B29', margin: 0 }}>Configured Branches</h3>
        </div>
        
        <div style={{ maxHeight: '68vh', overflowY: 'auto', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)', width: '100%', backgroundColor: '#ffffff', border: 'none' }}>
          <table className="premium-table" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead style={{ background: '#00d084' }}>
                  <tr>
                    <th style={{ padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', textAlign: 'left', border: 'none', whiteSpace: 'nowrap', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}>Company</th>
                    <th style={{ padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', textAlign: 'left', border: 'none', whiteSpace: 'nowrap' }}>Branch</th>
                    <th style={{ padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', textAlign: 'left', border: 'none', whiteSpace: 'nowrap' }}>Redeem Rate</th>
                    <th style={{ padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', textAlign: 'left', border: 'none', whiteSpace: 'nowrap' }}>All Items Earning</th>
                    <th style={{ padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', textAlign: 'left', border: 'none', whiteSpace: 'nowrap' }}>Max Limit</th>
                    <th style={{ textAlign: 'left', padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', border: 'none', whiteSpace: 'nowrap' }}>Currency</th>
                    <th style={{ textAlign: 'center', padding: '16px 20px', fontWeight: '700', fontSize: '13px', color: '#ffffff', border: 'none', borderTopRightRadius: '8px', borderBottomRightRadius: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settingsList.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>No reward settings configured yet.</td>
                    </tr>
                  ) : (
                    settingsList.map((setting) => (
                      <tr key={setting._id} className="premium-row" style={{ background: '#ffffff', transition: 'all 0.2s ease', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#334155', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>{setting.company_name || 'All'}</td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#334155', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>{setting.branch_name || 'All'}</td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#334155', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>1 pt = {setting.conversion_rate || setting.point_conversion_rate} {setting.currency}</td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#166534', fontWeight: '600', borderBottom: '1px solid #f1f5f9' }}>
                          {setting.points_per_currency > 0 ? `${setting.points_per_currency} pts / ${setting.spend_unit || 1} ${setting.currency}` : <span style={{ color: '#94a3b8' }}>Rules based</span>}
                        </td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#334155', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>{setting.max_redemption_limit > 0 ? setting.max_redemption_limit : 'Unlimited'}</td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#334155', fontWeight: '500', borderBottom: '1px solid #f1f5f9', textAlign: 'left' }}>{setting.currency}</td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                            <FaEdit 
                              onClick={() => handleEdit(setting)}
                              style={{ color: '#604BE8', cursor: 'pointer', fontSize: '16px' }}
                              title="Edit"
                            />
                            <FaTrash 
                              onClick={() => handleDelete(setting._id)}
                              style={{ color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
                              title="Delete"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
        </div>
      </div>

      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>Reward Settings Assignment</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {(isAdmin && companies.length > 1) && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#604BE8' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companies.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff2f9', borderRadius: '12px', border: '1.5px solid #C3CDE4', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => { setSelectedCompanies(prev => e.target.checked ? [...prev, comp] : prev.filter(c => c !== comp)); }} style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="assignment-card">
                <h4 style={{ color: '#06D6A0' }}><FaMapMarkerAlt /> Select Branch</h4>
                <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  {availableBranches.map((branch, idx) => (
                    <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                      <input type="checkbox" checked={selectedBranches.includes(branch)} onChange={(e) => { setSelectedBranches(prev => e.target.checked ? [...prev, branch] : prev.filter(b => b !== branch)); }} style={{ width: '18px', height: '18px', accentColor: '#06D6A0' }} />
                      {branch}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleSave} disabled={loading || selectedBranches.length === 0}>
                {loading ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardSettingsPage;
